import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { 
  ListTodo, Calendar, IndianRupee, Package, Clock, CheckCircle2, Circle, AlertCircle, 
  Filter, Search, Edit2, Save, X, ChevronDown, ChevronRight, GripVertical, FileSpreadsheet, CheckSquare, Square,
  MessageSquare, Camera, Upload, Image as ImageIcon
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import ProgressSlider from '@/components/ProgressSlider';

/**
 * VENDOR SCHEDULE - Unified Tasks & Schedule View
 * 
 * Combines Tasks and Schedule into one view (like Admin Schedule)
 * 
 * VENDOR CAN:
 * - View assigned tasks only
 * - Edit: Start date, End date, Progress status
 * - Drag progress slider
 * - Select tasks and convert to BOQ
 * - Chat with admin about specific tasks
 * - Upload images for task progress
 * 
 * VENDOR CANNOT:
 * - See base rate (only vendor rate)
 * - See client rate
 * - See other vendors' tasks
 * - See markup/markdown percentages
 */
export default function VendorSchedule() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editData, setEditData] = useState({
    start_date: '',
    end_date: '',
    progress_percentage: 0,
    status: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Task selection for Convert to BOQ
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [convertingToBOQ, setConvertingToBOQ] = useState(false);
  
  // Chat & Image Upload State (NEW)
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [chatTaskId, setChatTaskId] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageTaskId, setImageTaskId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageCaption, setImageCaption] = useState('');
  // iter-386u-d — View Entire BOQ toggle (default: only my assigned tasks).
  const [viewAll, setViewAll] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchVendorTasks();
  }, [viewAll]);

  const fetchVendorTasks = async () => {
    try {
      const response = await axios.get(`${API}/vendor/tasks${viewAll ? '?include_all=true' : ''}`);
      setTasks(response.data);
      // Auto-expand first project
      const projects = [...new Set(response.data.map(t => t.project_id))];
      if (projects.length > 0) {
        setExpandedProjects(new Set([projects[0]]));
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setEditData({
      start_date: task.start_date?.split('T')[0] || '',
      end_date: task.end_date?.split('T')[0] || '',
      progress_percentage: task.progress_percentage || 0,
      status: task.status || 'not_started'
    });
    setEditDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!selectedTask) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (editData.start_date) params.append('start_date', editData.start_date);
      if (editData.end_date) params.append('end_date', editData.end_date);
      params.append('progress_percentage', editData.progress_percentage);
      params.append('status', editData.status);
      
      await axios.put(`${API}/vendor/tasks/${selectedTask.id}?${params.toString()}`);
      toast.success('Task updated successfully');
      setEditDialogOpen(false);
      fetchVendorTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  // Quick progress update (without dialog)
  const handleQuickProgressUpdate = async (task, newProgress) => {
    try {
      const params = new URLSearchParams();
      params.append('progress_percentage', newProgress);
      
      // Auto-update status based on progress
      let newStatus = task.status;
      if (newProgress === 0) newStatus = 'not_started';
      else if (newProgress === 100) newStatus = 'completed';
      else if (newProgress > 0) newStatus = 'in_progress';
      params.append('status', newStatus);
      
      await axios.put(`${API}/vendor/tasks/${task.id}?${params.toString()}`);
      toast.success(`Progress updated to ${newProgress}%`);
      fetchVendorTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update progress');
    }
  };

  // Task selection handlers for Convert to BOQ
  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const toggleSelectAllInProject = (projectTasks) => {
    const projectTaskIds = projectTasks.map(t => t.id);
    const allSelected = projectTaskIds.every(id => selectedTasks.has(id));
    
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        projectTaskIds.forEach(id => newSet.delete(id));
      } else {
        projectTaskIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedTasks(new Set());
  };

  // Convert selected tasks to BOQ
  const handleConvertToBOQ = async () => {
    if (selectedTasks.size === 0) {
      toast.error('Please select at least one task');
      return;
    }

    setConvertingToBOQ(true);
    try {
      const selectedTasksArray = tasks.filter(t => selectedTasks.has(t.id));
      
      // Group by project to create separate BOQs if needed
      const tasksByProject = selectedTasksArray.reduce((acc, task) => {
        const pid = task.project_id;
        if (!acc[pid]) acc[pid] = [];
        acc[pid].push(task);
        return acc;
      }, {});

      for (const [projectId, projectTasks] of Object.entries(tasksByProject)) {
        const lineItems = projectTasks.map(task => ({
          task_id: task.id,
          task_title: task.name,
          category_name: task.category_name || 'General',
          category_code: task.category_code || '',
          unit: task.unit || 'Nos',
          quantity: task.quantity || 1,
          rate: task.rate || 0,
          gst_percentage: 18
        }));

        await axios.post(`${API}/boq-worksheets/from-tasks`, {
          project_id: projectId,
          line_items: lineItems
        });
      }

      toast.success(`Created BOQ from ${selectedTasks.size} task(s)`);
      clearSelection();
      fetchVendorTasks();
    } catch (error) {
      console.error('Convert to BOQ error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create BOQ');
    } finally {
      setConvertingToBOQ(false);
    }
  };

  // Chat with admin about a task (NEW)
  const openTaskChat = (task) => {
    setChatTaskId(task.id);
    setSelectedTask(task);
    setChatMessage('');
    setChatDialogOpen(true);
  };

  const handleSendChat = async () => {
    if (!chatMessage.trim() || !selectedTask) return;
    
    setSendingChat(true);
    try {
      await axios.post(`${API}/projects/${selectedTask.project_id}/chat`, {
        project_id: selectedTask.project_id,
        message: chatMessage,
        visibility: 'vendor',
        visible_to_vendor_id: user.id,
        task_id: selectedTask.id,
        task_title: `Task: ${selectedTask.name || selectedTask.title} - ${selectedTask.project_name}`
      });
      
      toast.success('Message sent to admin');
      setChatDialogOpen(false);
      setChatMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingChat(false);
    }
  };

  // Image upload for a task (NEW)
  const openImageUpload = (task) => {
    setImageTaskId(task.id);
    setSelectedTask(task);
    setImageCaption('');
    setImageDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTask) return;
    
    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.post(`${API}/tasks/${selectedTask.id}/images`, {
            filename: file.name,
            url: reader.result, // base64 data URL
            caption: imageCaption || `Progress update for ${selectedTask.name || selectedTask.title}`
          });
          
          toast.success('Image uploaded successfully! Admin will review it.');
          setImageDialogOpen(false);
          setImageCaption('');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to read image file');
      setUploadingImage(false);
    }
  };

  // Get unique projects for filter
  const projects = [...new Set(tasks.map(t => t.project_name))];
  const projectIds = [...new Set(tasks.map(t => t.project_id))];

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = !searchTerm || 
      task.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesProject = projectFilter === 'all' || task.project_name === projectFilter;
    
    return matchesSearch && matchesStatus && matchesProject;
  });

  // Group tasks by project
  const groupedByProject = filteredTasks.reduce((acc, task) => {
    const projectId = task.project_id;
    if (!acc[projectId]) {
      acc[projectId] = {
        id: projectId,
        name: task.project_name,
        tasks: []
      };
    }
    acc[projectId].tasks.push(task);
    return acc;
  }, {});

  // Calculate stats
  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    totalValue: tasks.reduce((sum, t) => sum + (t.total || 0), 0)
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle2 size={16} className="text-green-500" />;
      case 'in_progress': return <Clock size={16} className="text-blue-500" />;
      case 'not_started': return <Circle size={16} className="text-stone-400" />;
      default: return <AlertCircle size={16} className="text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'bg-green-100 text-green-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'not_started': 'bg-stone-100 text-stone-600',
      'delayed': 'bg-red-100 text-red-700',
      'blocked': 'bg-red-100 text-red-700'
    };
    return variants[status] || 'bg-stone-100 text-stone-600';
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-schedule-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">My Schedule</h1>
            <p className="text-stone-500 mt-1">Manage your assigned tasks and update progress</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Total Tasks</p>
            <p className="text-2xl font-bold text-stone-800">{stats.total}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Not Started</p>
            <p className="text-2xl font-bold text-stone-600">{stats.notStarted}</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-sm text-stone-500">Total Value</p>
            <p className="text-2xl font-bold text-purple-600">₹{stats.totalValue.toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-tasks"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]" data-testid="project-filter">
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* iter-386u-d — Vendor Data Visibility: View Entire BOQ toggle */}
          <button
            type="button"
            onClick={() => setViewAll((v) => !v)}
            className={`h-10 px-3 rounded-md text-xs font-semibold border transition-colors ${viewAll ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300 hover:border-stone-500'}`}
            data-testid="vendor-schedule-view-all-toggle"
            title={viewAll ? 'Showing every task in your projects — click to see only your assigned ones' : 'Currently showing only tasks assigned to you'}
          >
            {viewAll ? 'Showing Entire BOQ · Show My Tasks' : 'View Entire BOQ'}
          </button>
        </div>

        {/* Bulk Action Bar - appears when tasks are selected */}
        {selectedTasks.size > 0 && (
          <div className="apple-card p-4 bg-purple-50 border-purple-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare size={20} className="text-purple-600" />
              <span className="font-medium text-purple-800">
                {selectedTasks.size} task{selectedTasks.size > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={clearSelection}
                className="text-stone-600"
              >
                <X size={14} className="mr-1" />
                Clear
              </Button>
              <Button
                size="sm"
                onClick={handleConvertToBOQ}
                disabled={convertingToBOQ}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="convert-to-boq-btn"
              >
                <FileSpreadsheet size={14} className="mr-1" />
                {convertingToBOQ ? 'Converting...' : 'Convert to BOQ'}
              </Button>
            </div>
          </div>
        )}

        {/* Project Groups with Tasks */}
        {Object.keys(groupedByProject).length === 0 ? (
          <div className="apple-card p-12 text-center">
            <ListTodo size={48} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600 mb-2">No Tasks Assigned</h3>
            <p className="text-stone-400 text-sm">
              {tasks.length === 0 
                ? "You haven't been assigned any tasks yet."
                : "Try adjusting your search or filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.values(groupedByProject).map((project) => (
              <Collapsible 
                key={project.id}
                open={expandedProjects.has(project.id)}
                onOpenChange={() => toggleProject(project.id)}
              >
                <div className="apple-card overflow-hidden">
                  <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-stone-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <Package size={20} className="text-purple-600" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-stone-800">{project.name}</h3>
                        <p className="text-sm text-stone-500">
                          {project.tasks.length} task(s) | 
                          {project.tasks.filter(t => t.status === 'completed').length} completed
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Select All checkbox for this project */}
                      <div 
                        className="flex items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelectAllInProject(project.tasks);
                        }}
                      >
                        <Checkbox
                          checked={project.tasks.every(t => selectedTasks.has(t.id))}
                          onCheckedChange={() => toggleSelectAllInProject(project.tasks)}
                          className="border-stone-300 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                          data-testid={`select-all-project-${project.id}`}
                        />
                        <span className="text-xs text-stone-500">All</span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-purple-600">
                          ₹{project.tasks.reduce((sum, t) => sum + (t.total || 0), 0).toLocaleString()}
                        </p>
                      </div>
                      {expandedProjects.has(project.id) ? (
                        <ChevronDown size={20} className="text-stone-400" />
                      ) : (
                        <ChevronRight size={20} className="text-stone-400" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="border-t border-stone-100 divide-y divide-stone-50">
                      {project.tasks.map((task) => (
                        <div 
                          key={task.id}
                          className={`p-4 hover:bg-stone-50 transition-colors ${selectedTasks.has(task.id) ? 'bg-purple-50' : ''}`}
                          data-testid={`vendor-task-${task.id}`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Selection Checkbox */}
                            <div className="mt-1">
                              <Checkbox
                                checked={selectedTasks.has(task.id)}
                                onCheckedChange={() => toggleTaskSelection(task.id)}
                                className="border-stone-300 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                                data-testid={`select-task-${task.id}`}
                              />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {getStatusIcon(task.status)}
                                <h4 className="font-medium text-stone-800">{task.name}</h4>
                                <Badge className={getStatusBadge(task.status)}>
                                  {task.status?.replace('_', ' ')}
                                </Badge>
                                {task.category_name && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">
                                    {task.category_name}
                                  </span>
                                )}
                              </div>
                              
                              {/* Progress Slider (Draggable) — iter-386u-e disabled for context-only rows in "View Entire BOQ" mode */}
                              <div className="flex items-center gap-4 mt-3">
                                <div className="flex-1 max-w-md" style={{ opacity: task.assigned_to_me === false ? 0.4 : 1, pointerEvents: task.assigned_to_me === false ? 'none' : 'auto' }}>
                                  <ProgressSlider
                                    progress={task.progress_percentage || 0}
                                    onProgressChange={(value) => {
                                      if (task.assigned_to_me === false) {
                                        toast.info('This task is not assigned to you — switch to "Show My Tasks" to update yours');
                                        return;
                                      }
                                      handleQuickProgressUpdate(task, value);
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium text-stone-600 w-12 text-right">
                                  {task.progress_percentage || 0}%
                                </span>
                                {task.assigned_to_me === false && (
                                  <span className="text-[10px] uppercase font-bold text-stone-400 tracking-wide">
                                    Context only
                                  </span>
                                )}
                              </div>
                              
                              {/* Dates */}
                              {(task.start_date || task.end_date) && (
                                <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar size={14} />
                                    {task.start_date ? formatDate(task.start_date) : 'Not set'}
                                    {' → '}
                                    {task.end_date ? formatDate(task.end_date) : 'Not set'}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Rate & Actions */}
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              <div className="text-right">
                                <p className="text-sm text-stone-400">
                                  {task.quantity} {task.unit} × ₹{task.rate?.toLocaleString()}
                                </p>
                                <p className="font-bold text-purple-600">
                                  ₹{task.total?.toLocaleString()}
                                </p>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditTask(task)}
                                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                  data-testid={`edit-task-${task.id}`}
                                >
                                  <Edit2 size={14} className="mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openTaskChat(task)}
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  title="Chat with Admin"
                                  data-testid={`chat-task-${task.id}`}
                                >
                                  <MessageSquare size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openImageUpload(task)}
                                  className="text-green-600 border-green-300 hover:bg-green-50"
                                  title="Upload Progress Image"
                                  data-testid={`upload-image-${task.id}`}
                                >
                                  <Camera size={14} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}

        {/* Edit Task Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md" data-testid="edit-task-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl">
                Update Task
              </DialogTitle>
            </DialogHeader>
            
            {selectedTask && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="font-semibold text-stone-800">{selectedTask.name}</p>
                  <p className="text-sm text-stone-500">{selectedTask.project_name}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-700">Start Date</Label>
                    <Input
                      type="date"
                      value={editData.start_date}
                      onChange={(e) => setEditData({...editData, start_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-700">End Date</Label>
                    <Input
                      type="date"
                      value={editData.end_date}
                      onChange={(e) => setEditData({...editData, end_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-stone-700">Status</Label>
                  <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-stone-700">Progress: {editData.progress_percentage}%</Label>
                  <div className="mt-2">
                    <ProgressSlider
                      progress={editData.progress_percentage}
                      onProgressChange={(value) => setEditData({...editData, progress_percentage: value})}
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="text-stone-700">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTask}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Chat Dialog */}
        <Dialog open={chatDialogOpen} onOpenChange={setChatDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="text-blue-600" size={20} />
                Chat with Admin
              </DialogTitle>
              <DialogDescription>
                Send a message about: <strong>{selectedTask?.name || selectedTask?.title}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Your Message</Label>
                <Textarea
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Type your message to admin..."
                  className="mt-1 min-h-[120px]"
                  data-testid="task-chat-message"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setChatDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendChat}
                disabled={sendingChat || !chatMessage.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {sendingChat ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Image Upload Dialog */}
        <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="text-green-600" size={20} />
                Upload Progress Image
              </DialogTitle>
              <DialogDescription>
                Upload an image for: <strong>{selectedTask?.name || selectedTask?.title}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Caption (Optional)</Label>
                <Input
                  value={imageCaption}
                  onChange={(e) => setImageCaption(e.target.value)}
                  placeholder="Describe this progress update..."
                  className="mt-1"
                  data-testid="image-caption"
                />
              </div>
              
              <div>
                <Label>Select Image</Label>
                <div className="mt-2 border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="task-image-upload"
                    disabled={uploadingImage}
                  />
                  <label 
                    htmlFor="task-image-upload" 
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    {uploadingImage ? (
                      <>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        <span className="text-sm text-stone-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={32} className="text-stone-400" />
                        <span className="text-sm text-stone-600">Click to select image</span>
                        <span className="text-xs text-stone-400">Max 5MB, JPG/PNG</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
              
              <p className="text-xs text-stone-500">
                Your image will be visible to Admin immediately. 
                Once approved, it will appear in the Client Documents.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setImageDialogOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
